import * as React from "react";
import { Trash2, TriangleAlert } from "lucide-react";

import type { Brand } from "@/data/config";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** How long the trashcan must be held before the delete fires. */
const HOLD_MS = 5000;

/**
 * Drives a press-and-hold gesture: progress climbs from 0→1 over {@link HOLD_MS}
 * while held, snaps back to 0 on release, and fires `onComplete` once. Works for
 * mouse, touch and pen (pointer events) plus keyboard (Enter/Space).
 */
function useHoldToConfirm(onComplete: () => void) {
  const [progress, setProgress] = React.useState(0);
  const [holding, setHolding] = React.useState(false);
  const rafRef = React.useRef<number | null>(null);
  const startRef = React.useRef(0);
  const doneRef = React.useRef(false);
  const completeRef = React.useRef(onComplete);
  completeRef.current = onComplete;

  const stopRaf = React.useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = React.useCallback(
    (now: number) => {
      const p = Math.min(1, (now - startRef.current) / HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        doneRef.current = true;
        setHolding(false);
        stopRaf();
        completeRef.current();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [stopRaf],
  );

  const start = React.useCallback(() => {
    if (doneRef.current || rafRef.current !== null) return;
    setHolding(true);
    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const cancel = React.useCallback(() => {
    if (doneRef.current) return;
    stopRaf();
    setHolding(false);
    setProgress(0);
  }, [stopRaf]);

  /** Reset all state (e.g. when the dialog reopens for another brand). */
  const reset = React.useCallback(() => {
    stopRaf();
    doneRef.current = false;
    setHolding(false);
    setProgress(0);
  }, [stopRaf]);

  React.useEffect(() => () => stopRaf(), [stopRaf]);

  return { progress, holding, start, cancel, reset };
}

const RING_RADIUS = 56;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Second-stage brand-delete confirmation: a deliberately slow, hard-to-trigger
 * gesture. The owner must press and hold the trashcan for five seconds; a big
 * warning makes clear the deletion is irreversible and will break existing links.
 */
export function HoldToDeleteBrandDialog({
  brand,
  onOpenChange,
  onConfirm,
}: {
  brand: Brand | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (brand: Brand) => void;
}) {
  const { progress, holding, start, cancel, reset } = useHoldToConfirm(() => {
    if (brand) onConfirm(brand);
  });

  // Reset the gesture whenever the target brand changes or the dialog reopens.
  React.useEffect(() => {
    reset();
  }, [brand?.id, reset]);

  const secondsLeft = Math.ceil((1 - progress) * (HOLD_MS / 1000));

  return (
    <Dialog open={brand !== null} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-[rgba(255,90,90,.35)] bg-popover shadow-portal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#ff7a7a]">
            <TriangleAlert className="size-[18px]" />
            Permanently delete {brand?.name}?
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            This is <span className="font-semibold text-foreground">irreversible</span>. Every theme,
            member and setting for{" "}
            <span className="font-semibold text-foreground">{brand?.name}</span> is destroyed, and{" "}
            <span className="font-semibold text-foreground">
              any existing links, embeds or integrations pointing at it will break
            </span>{" "}
            and cannot be recovered.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-3">
          <div className="relative grid size-[136px] place-items-center">
            <svg width={136} height={136} className="-rotate-90" aria-hidden>
              <circle
                cx={68}
                cy={68}
                r={RING_RADIUS}
                className="fill-none stroke-border"
                strokeWidth={7}
              />
              <circle
                cx={68}
                cy={68}
                r={RING_RADIUS}
                className="fill-none stroke-[#ff5a5a]"
                strokeWidth={7}
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
                style={{ transition: holding ? "none" : "stroke-dashoffset 250ms ease-out" }}
              />
            </svg>

            <button
              type="button"
              aria-label={`Hold for ${HOLD_MS / 1000} seconds to delete ${brand?.name ?? "this brand"}`}
              onPointerDown={(e) => {
                e.preventDefault();
                // Capture so a pointer-up outside the button still cancels the
                // hold. Guarded: capture can throw for non-active pointer ids.
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch {
                  // ignore — the hold still works without capture
                }
                start();
              }}
              onPointerUp={cancel}
              onPointerCancel={cancel}
              onLostPointerCapture={cancel}
              onKeyDown={(e) => {
                if ((e.key === " " || e.key === "Enter") && !e.repeat) {
                  e.preventDefault();
                  start();
                }
              }}
              onKeyUp={(e) => {
                if (e.key === " " || e.key === "Enter") cancel();
              }}
              onContextMenu={(e) => e.preventDefault()}
              className="absolute grid size-[104px] cursor-pointer touch-none select-none place-items-center rounded-full border border-[rgba(255,90,90,.45)] bg-[rgba(255,90,90,.1)] text-[#ff7a7a] outline-none transition-[transform,background-color] duration-150 hover:bg-[rgba(255,90,90,.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(255,90,90,.4)] active:scale-[.97]"
            >
              <Trash2 className="size-9" strokeWidth={1.75} />
            </button>
          </div>

          <div className="text-center">
            <div className="text-[13.5px] font-semibold text-foreground">
              {holding ? `Keep holding… ${secondsLeft}s` : "Press and hold to delete"}
            </div>
            <div className="mt-0.5 text-[11.5px] text-text-3">
              Hold the trashcan for {HOLD_MS / 1000} seconds. Release to cancel.
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel — keep {brand?.name}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
